/**
 * Agent Function Knowledge Base API Client
 * Handles all API calls for AgentFunctionKnowledgeBase entities
 * (associates an AgentFunction binding with one or more knowledge-base collections)
 */

import { BaseApiClient, ApiResponse } from './baseApiClient';
import type { StandardListRequest, GetByIdRequest } from '../types';

export interface AgentFunctionKnowledgeBase {
  AgentFunctionKnowledgeBaseID: number;
  AgentFunctionID: number;
  DocumentCollectionID: number | null;
  CollectionNameTemplate: string | null;
  Usage: string; // "Primary" | "Secondary" | "Fallback", not DB-enforced, just these expected values
  Priority: number;
  Profiles: string | null; // comma-delimited, e.g. "STARTUP,SMB"
  IsActive: boolean;
  // Agent-driven RAG retrieval pipeline (2026-09-02) -- keyed-DI selector names, resolved by
  // BizFirst.Ai.Octopus.Plugin.KnowledgeBase's AgentFunctionKnowledgeRetriever. Null means "not
  // configured here", falls through to the system default ("NoOp" for both).
  QueryRewriterName: string | null; // "NoOp" | "Llm", the only two registered keys
  RerankerName: string | null; // "NoOp" | "Llm", the only two registered keys
  // How many of the most recent live-conversation dialog turns feed the query rewriter's
  // RecentConversationTurns. Null means "not configured here", falls through to the system
  // default of 3 turns.
  RecentConversationTurnsCount: number | null;
  CreatedOn?: string;
}

export class AgentFunctionKnowledgeBaseApiClient extends BaseApiClient {
  /**
   * Get all agent function knowledge bases (standard list)
   */
  async getList(request: StandardListRequest = {}): Promise<ApiResponse<AgentFunctionKnowledgeBase[]>> {
    const requestWithPagination = {
      PageInfo: {
        PageNumber: 1,
        PageSize: 1000,
      },
      ...request,
    };
    return this.postQuery<AgentFunctionKnowledgeBase[]>('/api/v1/ai-function/agent-function-knowledge-bases/list', requestWithPagination);
  }

  /**
   * Get agent function knowledge base by ID
   */
  async getById(request: GetByIdRequest): Promise<ApiResponse<AgentFunctionKnowledgeBase>> {
    const webRequest = {
      ID: {
        ID: request.id,
      },
    };
    return this.postQuery<AgentFunctionKnowledgeBase>(`/api/v1/ai-function/agent-function-knowledge-bases/${request.id}`, webRequest);
  }

  /**
   * Get all knowledge base associations for a given AgentFunctionID
   */
  async getByAgentFunctionId(
    agentFunctionId: number,
    request: StandardListRequest = {}
  ): Promise<ApiResponse<AgentFunctionKnowledgeBase[]>> {
    const requestWithPagination = {
      AgentFunctionID: agentFunctionId,
      PageInfo: {
        PageNumber: 1,
        PageSize: 1000,
      },
      ...request,
    };
    return this.postQuery<AgentFunctionKnowledgeBase[]>(
      `/api/v1/ai-function/agent-function-knowledge-bases/agent-function/${agentFunctionId}`,
      requestWithPagination
    );
  }

  /**
   * Create new agent function knowledge base association
   */
  async create(knowledgeBase: any): Promise<ApiResponse<AgentFunctionKnowledgeBase>> {
    const response = await this.post<any>('/api/v1/ai-function/agent-function-knowledge-bases', knowledgeBase);

    // Handle response structure - API returns id in response.id.id or response.data.Id.id
    if (response.success) {
      let agentFunctionKnowledgeBaseId = null;

      // Try to extract ID from various possible locations
      const r = response as any;
      if (r.id?.id) {
        agentFunctionKnowledgeBaseId = r.id.id;
      } else if (response.data?.id?.id) {
        agentFunctionKnowledgeBaseId = response.data.id.id;
      } else if (response.data?.Id?.id) {
        agentFunctionKnowledgeBaseId = response.data.Id.id;
      }

      if (agentFunctionKnowledgeBaseId) {
        return {
          success: true,
          data: {
            AgentFunctionKnowledgeBaseID: agentFunctionKnowledgeBaseId,
            ...knowledgeBase,
            IsActive: knowledgeBase.IsActive ?? true,
          } as AgentFunctionKnowledgeBase,
        };
      }
    }

    // If we couldn't extract the ID but still got success, return what we have
    if (response.success && !response.data?.AgentFunctionKnowledgeBaseID) {
      return {
        success: true,
        data: {
          AgentFunctionKnowledgeBaseID: 0, // Fallback - will be updated from actual data
          ...knowledgeBase,
          IsActive: knowledgeBase.IsActive ?? true,
        } as AgentFunctionKnowledgeBase,
      };
    }

    return response;
  }

  /**
   * Update existing agent function knowledge base association
   */
  async update(id: number, knowledgeBase: any): Promise<ApiResponse<AgentFunctionKnowledgeBase>> {
    const webRequest = {
      Data: {
        AgentFunctionKnowledgeBaseID: id,
        ...knowledgeBase,
      },
    };
    return this.put<AgentFunctionKnowledgeBase>(`/api/v1/ai-function/agent-function-knowledge-bases/${id}`, webRequest);
  }

  /**
   * Delete agent function knowledge base association (soft delete)
   */
  async deleteById(id: number): Promise<ApiResponse<void>> {
    const webRequest = {
      ID: {
        ID: id,
      },
    };
    return this.delete<void>(`/api/v1/ai-function/agent-function-knowledge-bases/${id}`, webRequest);
  }
}

export const agentFunctionKnowledgeBaseApiClient = new AgentFunctionKnowledgeBaseApiClient();
